import type { CompositionV2DataQuery } from '@revelio/data-access';

import type { CompositionMetricValues } from '../../PageConfigProvider/types/metric';
import {
  type BreakdownData,
  type BreakdownSingleDataSet,
  DataType,
  type SingleDataPoint,
  type SingleDataSet,
} from '../types';

type MultiCompositionData = {
  dim: string;
  data: CompositionV2DataQuery['composition_v2'];
};

export type CompositionTopNDataSetType = SingleDataSet | BreakdownSingleDataSet;

export const convertCompositionTopNData = ({
  data,
  isGrouped,
  metric,
  exchangeRate = 1,
}: {
  data: MultiCompositionData[];
  isGrouped?: boolean;
  metric: CompositionMetricValues;
  exchangeRate?: number;
}): CompositionTopNDataSetType => {
  if (isGrouped) {
    const compositionData: BreakdownSingleDataSet['data'] = data?.map(
      (entityGroup): BreakdownData<SingleDataPoint> => ({
        dim: entityGroup.dim,
        breakdown:
          entityGroup.data?.entities?.map((entity) => ({
            dim: entity.metadata?.short_name,
            value: getConvertedValue({
              metric,
              exchangeRate,
              entityMetric: entity.metrics?.[metric],
            }),
            share: getConvertedValue({
              metric,
              exchangeRate,
              entityMetric: entity.metrics?.[metric],
            }),
          })) ?? [],
      })
    );

    return { type: DataType.BREAKDOWN_SINGLE, data: compositionData };
  }

  const barDataObj =
    data?.reduce<Record<string, SingleDataPoint>>((acc, { data }) => {
      data?.entities?.forEach((entity) => {
        const metadata = entity.metadata;
        const metricData = entity.metrics?.[metric];
        const lastTimeseries = metricData?.[metricData.length - 1];
        const convertedValue =
          typeof lastTimeseries?.value === 'number' && metric === 'salary'
            ? lastTimeseries.value * exchangeRate
            : (lastTimeseries?.value ?? null);

        const innerDim = metadata?.short_name;
        if (!acc[innerDim]) {
          acc[innerDim] = {
            dim: innerDim,
            value: convertedValue,
          };
        } else {
          acc[innerDim] = {
            ...acc[innerDim],
            value: (acc[innerDim]?.value ?? 0) + (convertedValue ?? 0),
          };
        }
      });
      return acc;
    }, {}) ?? [];

  const barData: SingleDataSet['data'] = Object.values(barDataObj).sort(
    (a, b) => (b.value ?? 0) - (a.value ?? 0)
  );

  return { type: DataType.SINGLE, data: barData };
};

const getConvertedValue = ({
  metric,
  exchangeRate,
  entityMetric,
}: {
  metric: CompositionMetricValues;
  exchangeRate: number;
  entityMetric: NonNullable<
    CompositionV2DataQuery['composition_v2']
  >['entities'][number]['metrics'][CompositionMetricValues];
}): number | null => {
  const lastMetric = entityMetric?.[entityMetric.length - 1]?.value ?? null;
  if (typeof lastMetric !== 'number') return lastMetric;
  if (metric !== 'salary') return lastMetric;
  return lastMetric * exchangeRate;
};
